/**
 * Passkey (WebAuthn) client helpers — V1.9.8.
 *
 * Wraps the raw browser APIs (`navigator.credentials.create` / `.get`) and
 * marshals base64url ↔ Uint8Array so the JSON contract with the backend
 * stays clean. All functions throw on user cancel / unsupported browser
 * so the calling UI can surface a friendly toast.
 */

import api from "@/lib/api";

// ---------- base64url helpers ----------
const b64ToBytes = (s) => {
    const pad = "=".repeat((4 - (s.length % 4)) % 4);
    const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + pad);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return arr;
};

const bytesToB64 = (buf) => {
    const bytes = new Uint8Array(buf);
    let s = "";
    for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
    return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

// ---------- feature detect ----------
export function passkeysSupported() {
    return (
        typeof window !== "undefined" &&
        window.isSecureContext &&
        !!window.PublicKeyCredential
    );
}

/** Detect whether this device has any platform authenticator (Face ID, Touch
 *  ID, Windows Hello, Android fingerprint). Used to hide the "Sign in with
 *  passkey" button on machines that can't fulfill it. Non-blocking — returns
 *  false on any error / on browsers that don't ship the check. */
export async function hasPlatformAuthenticator() {
    if (!passkeysSupported()) return false;
    try {
        return await window.PublicKeyCredential
            .isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
        return false;
    }
}

// ---------- transform server options into WebAuthn args ----------
function toCreationOptions(o) {
    return {
        ...o,
        challenge: b64ToBytes(o.challenge),
        user: { ...o.user, id: b64ToBytes(o.user.id) },
        excludeCredentials: (o.excludeCredentials || []).map((c) => ({
            ...c,
            id: b64ToBytes(c.id),
        })),
    };
}

function toRequestOptions(o) {
    return {
        ...o,
        challenge: b64ToBytes(o.challenge),
        allowCredentials: (o.allowCredentials || []).map((c) => ({
            ...c,
            id: b64ToBytes(c.id),
        })),
    };
}

// ---------- transform browser credential into JSON for the server ----------
function registrationToJSON(cred) {
    return {
        id: cred.id,
        rawId: bytesToB64(cred.rawId),
        type: cred.type,
        response: {
            clientDataJSON: bytesToB64(cred.response.clientDataJSON),
            attestationObject: bytesToB64(cred.response.attestationObject),
            transports: cred.response.getTransports
                ? cred.response.getTransports()
                : [],
        },
        clientExtensionResults: cred.getClientExtensionResults(),
        authenticatorAttachment: cred.authenticatorAttachment || null,
    };
}

function authenticationToJSON(cred) {
    return {
        id: cred.id,
        rawId: bytesToB64(cred.rawId),
        type: cred.type,
        response: {
            clientDataJSON: bytesToB64(cred.response.clientDataJSON),
            authenticatorData: bytesToB64(cred.response.authenticatorData),
            signature: bytesToB64(cred.response.signature),
            userHandle: cred.response.userHandle
                ? bytesToB64(cred.response.userHandle)
                : null,
        },
        clientExtensionResults: cred.getClientExtensionResults(),
        authenticatorAttachment: cred.authenticatorAttachment || null,
    };
}

// ---------- public API ----------

/** Enroll a new passkey for the logged-in user. Requires an existing JWT
 *  cookie (call after password login). Returns `{ ok, credential_id, label }`.
 */
export async function registerPasskey() {
    if (!passkeysSupported()) {
        throw new Error("Passkeys are not supported on this browser or over HTTP.");
    }
    const opts = (await api.post("/passkeys/register/options")).data;
    let cred;
    try {
        cred = await navigator.credentials.create({
            publicKey: toCreationOptions(opts),
        });
    } catch (e) {
        if (e && e.name === "NotAllowedError") {
            throw new Error("Passkey enrollment was cancelled.");
        }
        throw new Error(e?.message || "Could not create passkey.");
    }
    if (!cred) throw new Error("No credential returned by browser.");
    const verify = await api.post(
        "/passkeys/register/verify",
        registrationToJSON(cred),
    );
    return verify.data;
}

/** Sign in using a passkey. Requires HTTPS + a platform authenticator with a
 *  stored discoverable credential for this RP. On success the backend has
 *  already issued `access_token` + `refresh_token` cookies; the caller
 *  should refresh the AuthContext and route into the app. Returns the user
 *  object `{ id, email, name, role }`. */
export async function loginWithPasskey() {
    if (!passkeysSupported()) {
        throw new Error("Passkeys are not supported on this browser or over HTTP.");
    }
    const opts = (await api.post("/passkeys/login/options")).data;
    let cred;
    try {
        cred = await navigator.credentials.get({
            publicKey: toRequestOptions(opts),
        });
    } catch (e) {
        if (e && e.name === "NotAllowedError") {
            throw new Error("Passkey sign-in was cancelled.");
        }
        throw new Error(e?.message || "Could not use passkey.");
    }
    if (!cred) throw new Error("No credential returned by browser.");
    const res = await api.post("/passkeys/login/verify", {
        challenge_id: opts.challenge_id,
        credential: authenticationToJSON(cred),
    });
    return res.data;
}

/** List the current user's enrolled passkeys. */
export async function listPasskeys() {
    return (await api.get("/passkeys")).data;
}

/** Remove a passkey by credential_id. */
export async function deletePasskey(credentialId) {
    return (await api.delete(`/passkeys/${encodeURIComponent(credentialId)}`)).data;
}
